import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import branding from '../../styles/branding';
import './Auth.css';

const jllLogoUrl = '/jll-logo.png';

export const Login = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authState?.isAuthenticated) {
      navigate('/');
    }
  }, [authState, navigate]);

  const handleLogin = async () => {
    try {
      await oktaAuth.signInWithRedirect({ originalUri: '/' });
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  if (!authState) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#fff' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundImage: 'url(/images/login_bg/login_bg2.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'left',
            color: 'white',
            p: 4,
            maxWidth: '80%',
            marginLeft: '10%',
          }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 4,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontFamily: branding.fonts.primary,
            }}
          >
            JLL Survey Analytics
          </Typography>
          <Typography 
            variant="h5"
            sx={{ 
              lineHeight: 1.6,
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontFamily: branding.fonts.primary,
            }}
          >
            Transforming Survey Data into<br />
            Actionable Insights
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
          <Box
            component="img"
            src={jllLogoUrl}
            alt="JLL Logo"
            sx={{
              height: 60,
              mb: 3,
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: branding.fonts.primary,
              fontWeight: 600,
              color: branding.colors.primary,
              textAlign: 'center',
            }}
          >
            Survey AI Analytics
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              mb: 4,
              color: '#666',
              textAlign: 'center',
              fontFamily: branding.fonts.primary,
            }}
          >
            Turning Responses into Strategic Insights
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            fullWidth
            sx={{
              ...branding.button.sx,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: '30px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Sign In with Okta
          </Button>

          <Typography
            variant="body2"
            sx={{
              mt: 3,
              color: '#888',
              textAlign: 'center',
              fontSize: '0.875rem',
            }}
          >
            Use your organization credentials to access the application
          </Typography>

          <Typography
            variant="caption"
            sx={{
              mt: 4,
              color: '#aaa',
              textAlign: 'center',
            }}
          >
            © {new Date().getFullYear()} JLL. All rights reserved.
          </Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
